// Parent account settings. Track C.
//
// Fills a real gap: the API has had password-reset and magic-link since M1,
// but a signed-in parent had no screen to change their own password or email,
// and no way to exercise the export/delete rights CLAUDE.md §4 requires under
// Qatar PDPL. Five sections, one page, no sub-routes — a settings screen that
// makes you navigate is a settings screen people never finish.
//
// Parent-only. Kids have no email and must never see an erasure control; the
// teacher and school-admin shells are out of MVP scope (schools stay dark).
//
// RTL: logical utilities only. The two fixed-ASCII inputs (email, the delete
// confirmation word) carry dir="ltr" so the caret and the text agree under
// Arabic chrome.

import { useT } from '@fajrarabi/i18n/react';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';

import { AccountSection } from '~/components/account/AccountSection';
import { ChangeEmailCard } from '~/components/account/ChangeEmailCard';
import { ChangePasswordCard } from '~/components/account/ChangePasswordCard';
import { DeleteAccountCard } from '~/components/account/DeleteAccountCard';
import { ExportDataCard } from '~/components/account/ExportDataCard';
import { LanguagePreferenceCard } from '~/components/account/LanguagePreferenceCard';
import { useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';
import { shellForContext } from '~/lib/roles';

export const Route = createFileRoute('/$locale/account')({
  component: AccountScreen,
});

function AccountScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { locale: rawLocale } = useParams({ from: '/$locale/account' });
  const locale = asLocale(rawLocale);
  const { currentRoleContext, loading } = useAuth();

  const isParent = shellForContext(currentRoleContext) === 'parent';

  // Same guard shape as /progress: bounce non-parents home rather than
  // rendering a half-usable screen. Wait for the bootstrap first, or the
  // redirect fires before the role context has loaded.
  useEffect(() => {
    if (!loading && !isParent) {
      void navigate({ to: '/$locale', params: { locale }, replace: true });
    }
  }, [loading, isParent, navigate, locale]);

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-10">
        <AccountSection id="account-loading" title={t('app.common.loading')}>
          <div className="h-24 animate-pulse rounded-2xl bg-cream-100" />
        </AccountSection>
      </div>
    );
  }

  if (!isParent) return null;

  return (
    <div className="mx-auto max-w-2xl px-6 py-10">
      <header>
        <h1 className="text-2xl font-bold text-ink-900">{t('app.account.title')}</h1>
        <p className="mt-1 text-sm text-ink-500">{t('app.account.subtitle')}</p>
      </header>

      <div className="mt-8">
        <ChangePasswordCard />
        <ChangeEmailCard />
        <LanguagePreferenceCard locale={locale} />
        <ExportDataCard />
        {/* Last, and visually separated — destructive and irreversible. */}
        <DeleteAccountCard locale={locale} />
      </div>
    </div>
  );
}
